/**
 * Calmy video player - drives snippets/calmy-video-controls.liquid.
 *
 * One overlay per <video>. Native controls are switched off on these videos
 * (and only these), so the browser never draws a second Play button of its
 * own over ours. They come back inside fullscreen, where the overlay is not
 * visible.
 *
 *   idle / paused   one centred Play button
 *   playing         bar: pause, sound, fullscreen. A tap on the picture pauses.
 *   ended           the centred button becomes Replay
 *   error           the centred button becomes Retry, with a short message
 *
 * The UI follows the video's events, never the click. Starting one clip pauses
 * the others in the same group ([data-cvp-group]). Each overlay is bound once,
 * so a theme-editor section reload never stacks listeners.
 */
(function () {
  "use strict";

  function setState(ui, state) {
    var play = ui.querySelector("[data-cvp-play]");
    var bar = ui.querySelector("[data-cvp-bar]");
    ui.classList.remove("is-playing", "is-ended", "is-error", "is-loading");
    if (state !== "idle" && state !== "paused") ui.classList.add("is-" + state);
    var showPlay = state !== "playing";
    play.hidden = !showPlay;
    bar.hidden = state !== "playing";
    var label = state === "ended" ? play.getAttribute("data-label-replay")
      : state === "error" ? play.getAttribute("data-label-retry")
      : play.getAttribute("data-label-play");
    if (label) play.setAttribute("aria-label", label);
    play.disabled = state === "loading";
  }

  function bind(ui) {
    if (ui.__cvp) return;
    var video = ui.parentElement && ui.parentElement.querySelector("video");
    if (!video) return;
    ui.__cvp = true;

    video.controls = false;
    video.removeAttribute("controls");
    video.playsInline = true;
    video.setAttribute("playsinline", "");
    video.setAttribute("webkit-playsinline", "");

    var play = ui.querySelector("[data-cvp-play]");
    var pause = ui.querySelector("[data-cvp-pause]");
    var mute = ui.querySelector("[data-cvp-mute]");
    var fs = ui.querySelector("[data-cvp-fs]");
    var failed = false;

    function syncMute() {
      mute.setAttribute("aria-pressed", video.muted ? "true" : "false");
      mute.setAttribute("aria-label", video.muted ? "Turn sound on" : "Turn sound off");
    }

    function start() {
      if (failed) { failed = false; video.load(); }
      if (video.ended) video.currentTime = 0;
      setState(ui, "loading");
      var p;
      try { p = video.play(); } catch (e) { setState(ui, "error"); failed = true; return; }
      if (p && typeof p.then === "function") {
        p.catch(function (err) {
          if (err && err.name === "AbortError") return;   /* paused before it began */
          if (!video.muted) {
            /* Usually the browser refusing sound: muted playback is allowed. */
            video.muted = true; syncMute();
            video.play().catch(function () { failed = true; setState(ui, "error"); });
          } else { failed = true; setState(ui, "error"); }
        });
      }
    }

    play.addEventListener("click", function (e) { e.preventDefault(); start(); });
    pause.addEventListener("click", function (e) { e.preventDefault(); video.pause(); });
    mute.addEventListener("click", function (e) { e.preventDefault(); video.muted = !video.muted; syncMute(); });

    var canFs = !!(video.requestFullscreen || video.webkitRequestFullscreen || video.webkitEnterFullscreen);
    if (!canFs) fs.hidden = true;
    fs.addEventListener("click", function (e) {
      e.preventDefault();
      if (video.requestFullscreen) video.requestFullscreen().catch(function () {});
      else if (video.webkitRequestFullscreen) video.webkitRequestFullscreen();
      else if (video.webkitEnterFullscreen) video.webkitEnterFullscreen();   /* iPhone */
    });
    /* Inside fullscreen the overlay is not shown, so hand the browser its
       controls for that time only. */
    function onFs() {
      var fsEl = document.fullscreenElement || document.webkitFullscreenElement;
      video.controls = fsEl === video;
    }
    document.addEventListener("fullscreenchange", onFs);
    document.addEventListener("webkitfullscreenchange", onFs);
    video.addEventListener("webkitendfullscreen", function () { video.controls = false; });

    /* A tap on the picture while it plays pauses it. */
    video.addEventListener("click", function () { if (!video.paused) video.pause(); });

    video.addEventListener("playing", function () { failed = false; setState(ui, "playing"); });
    video.addEventListener("pause", function () { if (!video.ended) setState(ui, failed ? "error" : "paused"); });
    video.addEventListener("ended", function () { setState(ui, "ended"); });
    video.addEventListener("error", function () { failed = true; setState(ui, "error"); });
    video.addEventListener("volumechange", syncMute);

    /* One clip at a time within its group. */
    video.addEventListener("play", function () {
      var group = ui.closest("[data-cvp-group]") || document;
      group.querySelectorAll("video").forEach(function (o) { if (o !== video && !o.paused) o.pause(); });
    });

    syncMute();
    setState(ui, !video.paused && !video.ended ? "playing" : video.ended ? "ended" : "idle");

    /* Out of view or tab hidden: pause (never rewind). */
    if ("IntersectionObserver" in window) {
      new IntersectionObserver(function (entries) {
        entries.forEach(function (en) { if (!en.isIntersecting && !video.paused) video.pause(); });
      }, { threshold: 0.25 }).observe(video);
    }
  }

  document.addEventListener("visibilitychange", function () {
    if (!document.hidden) return;
    document.querySelectorAll("[data-cvp]").forEach(function (ui) {
      var v = ui.parentElement && ui.parentElement.querySelector("video");
      if (v && !v.paused) v.pause();
    });
  });

  function boot(root) { (root || document).querySelectorAll("[data-cvp]").forEach(bind); }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", function () { boot(); });
  else boot();
  document.addEventListener("shopify:section:load", function (e) { boot(e.target); });
})();
